import { TriangleAlert } from "lucide-react";

import { distancia, hectareas, media as calcularMedia, numero } from "@/lib/formatoDeMercado";
import {
  ajustesEfectivos,
  estadisticaDeLaMuestra,
  valorHomologado,
  type AjusteDeHomologacion,
} from "@/lib/homologacion";
import type { ClaseDeEvidencia, ReferenciaDelInforme } from "@/lib/referencias";
import { cn } from "@/lib/utils";

interface ResumenDeReferenciasProps {
  /** Las de ESTA pestaña, ya sin las descartadas. */
  referencias: ReferenciaDelInforme[];
  escala: AjusteDeHomologacion[];
  clase: ClaseDeEvidencia;
  /** La del predio TASADO, de la que sale el valor total derivado. */
  superficieDelPredioHa: number | null;
}

/**
 * Lo que este informe se llevó del mercado, resumido en una cifra.
 *
 * ES EL HERMANO DEL PANEL DEL ENLAZADOR Y RESUME OTRA COSA. Aquel describe una
 * SELECCIÓN en curso y su media va explícitamente SIN homologar —lo dice su
 * rótulo— porque ahí el tasador todavía está eligiendo. Este describe lo que el
 * informe YA tiene y su media va CON los ajustes puestos, que es justamente el
 * trabajo que se hace en esta pantalla. Los dos rótulos dicen cuál es cuál: con
 * la misma palabra, dos pantallas del mismo flujo mostrarían números distintos
 * sin explicar por qué.
 *
 * ES UNA MEDIA Y NO UNA MEDIANA, igual que el panel del enlazador: acá el
 * conjunto es un puñado de fichas revisadas a mano, y cómo se resume un conjunto
 * de comparables lo fija quien tasa. La mediana queda para el gráfico del
 * catálogo, que describe miles de fichas que nadie eligió.
 *
 * SIGUE SIN SER UNA TASACIÓN. El valor total del predio es la media aplicada a
 * su superficie, no el resultado del informe: faltan los derechos de agua, las
 * construcciones y las plantaciones.
 */
export function ResumenDeReferencias({
  referencias,
  escala,
  clase,
  superficieDelPredioHa,
}: ResumenDeReferenciasProps) {
  // Un panel con todo en "—" al lado de una lista vacía es ruido: la pestaña ya
  // dice que el informe no tiene referencias de esta clase.
  if (referencias.length === 0) return null;

  /* Cada ficha con SUS DOS CIFRAS: lo que decía el catálogo y lo que queda tras
     homologarla. Se arma una sola vez y alimenta tanto los agregados de arriba
     como el desglose del pie, para que no puedan discrepar. */
  const filas = referencias.map((referencia) => {
    const original = ufHa(referencia);

    return {
      referencia,
      original,
      homologado: valorHomologado(
        original,
        ajustesEfectivos(referencia.ajustes ?? [], escala, clase),
      ),
    };
  });

  const homologados = filas.map((f) => f.homologado);

  const mediaUfHa = calcularMedia(homologados);
  const mediaOriginal = calcularMedia(filas.map((f) => f.original));
  const conValor = homologados.filter((v): v is number => v !== null);
  const estadistica = estadisticaDeLaMuestra(conValor);

  /* Sin superficie del predio se muestra "—" y NUNCA cero: un informe puede
     estar `incompleto` (RF-E1 §8.3), y un cero se lee como una medición en vez
     de como el dato que falta. */
  const valorTotalUf =
    mediaUfHa === null || superficieDelPredioHa === null || superficieDelPredioHa <= 0
      ? null
      : mediaUfHa * superficieDelPredioHa;

  const validadas = referencias.filter((r) => r.validada).length;

  /* SIN AJUSTES GUARDADOS es "todavía no se homologó". Se mira lo GUARDADO y no
     lo efectivo: la fuente derivada la pone la pantalla y estaría en todas, así
     que contarla haría que ninguna ficha figurara nunca como pendiente. */
  const sinHomologar = referencias.filter((r) => (r.ajustes ?? []).length === 0).length;

  const distancias = referencias
    .map((r) => r.distancia_m)
    .filter((d): d is number => d !== null);
  const distanciaPromedio =
    distancias.length === 0
      ? null
      : distancias.reduce((suma, d) => suma + d, 0) / distancias.length;

  return (
    /* Sobre fondo oscuro y con `superficie-fija`, NO `bg-ink`: aquel se
       INVIERTE en modo oscuro y dejaría el panel blanco sobre blanco. El diseño
       lo quiere oscuro siempre, y por eso el token tampoco se invierte. Es el
       mismo tratamiento que el panel del enlazador, para que las dos pantallas
       del flujo se reconozcan. */
    <div className="bg-superficie-fija text-superficie-fija-foreground rounded-2xl p-4">
      <div className="text-superficie-fija-muted flex items-center justify-between gap-2 text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase">
        <span>Valor de referencia</span>
        <span className="text-superficie-fija-ghost font-mono tabular-nums">
          {referencias.length} {referencias.length === 1 ? "ficha" : "fichas"}
        </span>
      </div>

      {/* La cifra y su unidad en la MISMA línea, alineadas por su base: en dos
          renglones la unidad se lee como una bajada y la cifra queda sin decir
          de qué es. */}
      <div className="mt-3 flex items-baseline gap-2.5">
        <span className="font-mono text-[1.6875rem] leading-none font-bold tracking-[-0.03em] tabular-nums">
          {mediaUfHa === null ? "—" : numero(mediaUfHa, 0)}
        </span>
        <span className="text-superficie-fija-muted text-[0.78125rem] font-bold">
          UF/ha media
        </span>
      </div>

      <p className="text-superficie-fija-dim mt-1.5 text-xs font-semibold">
        Media de las referencias, con ajustes de homologación
      </p>

      <dl className="border-superficie-fija-linea mt-4 flex flex-col gap-2 border-t pt-3.5 text-[0.78125rem] font-semibold">
        <Fila
          rotulo="Rango UF/ha"
          valor={
            conValor.length === 0
              ? "—"
              : `${numero(Math.min(...conValor), 0)} – ${numero(Math.max(...conValor), 0)}`
          }
        />
        {/* La estadística de la muestra (gemelo de la que persiste el api). Con
            menos de dos fichas no hay desviación ni rango: guion y nunca cero. */}
        <Fila
          rotulo="Desviación estándar"
          valor={estadistica?.desviacion == null ? "—" : numero(estadistica.desviacion, 0)}
        />
        <Fila
          rotulo="Intervalo de confianza 95 %"
          valor={estadistica?.ic95 == null ? "—" : `± ${numero(estadistica.ic95, 0)}`}
        />
        <Fila
          rotulo="Rango comercial estadístico"
          valor={
            estadistica?.limiteInferior == null || estadistica.limiteSuperior == null
              ? "—"
              : `${numero(estadistica.limiteInferior, 0)} – ${numero(estadistica.limiteSuperior, 0)}`
          }
        />
        <Fila
          rotulo="Superficie total"
          valor={`${hectareas(
            referencias.reduce((suma, r) => suma + (Number(r.superficie_terreno_m2) || 0), 0),
          )} ha`}
        />
        <Fila
          rotulo="Valor total del predio"
          // El verde marca que es una cifra DERIVADA y no un dato leído de las
          // fichas, que es lo que son las otras.
          valor={valorTotalUf === null ? "—" : `${numero(valorTotalUf, 0)} UF`}
          className={valorTotalUf === null ? undefined : "text-superficie-fija-exito"}
        />
        <Fila rotulo="Distancia promedio" valor={distancia(distanciaPromedio)} />
        <Fila
          rotulo="Datos validados"
          valor={`${validadas} de ${referencias.length}`}
          className={
            validadas === referencias.length
              ? "text-superficie-fija-exito"
              : "text-superficie-fija-aviso"
          }
        />
      </dl>

      {/* EL DESGLOSE: de dónde sale la cifra grande.
          La media vive acá y los valores viven en las tarjetas de la izquierda,
          así que sin esta tabla las dos cosas quedan tan separadas que no se
          reconoce que una sale de la otra (Rodgers, 10-09-2026).

          NO usa el `Table` del DS y no es un descuido: aquel pinta con
          `text-foreground`, que se INVIERTE con el tema, y esta superficie no
          invierte a propósito — quedaría texto claro sobre fondo claro, el
          mismo defecto que ya tuvo `bg-ink` acá. Dentro del panel manda la
          paleta `superficie-fija`.

          `table-fixed` para que el título largo se recorte en su columna en vez
          de empujar a las cifras fuera del panel, que mide 19,5rem. */}
      <table
        aria-label="Valor original y homologado de cada referencia"
        className="border-superficie-fija-linea mt-4 w-full table-fixed border-t pt-3.5 text-[0.6875rem] font-semibold"
      >
        <thead>
          <tr className="text-superficie-fija-rotulo text-[0.625rem] font-extrabold tracking-[0.05em] uppercase">
            <th scope="col" className="py-2 text-left font-extrabold">
              Ficha
            </th>
            <th scope="col" className="w-[4.5rem] py-2 text-right font-extrabold">
              Original
            </th>
            <th scope="col" className="w-[4.5rem] py-2 text-right font-extrabold">
              Homolog.
            </th>
          </tr>
        </thead>

        <tbody>
          {filas.map(({ referencia, original, homologado }) => (
            <tr key={referencia.id} className="border-superficie-fija-linea border-t">
              <th
                scope="row"
                className="text-superficie-fija-ghost truncate py-1.5 pr-2 text-left font-semibold"
                title={referencia.titulo}
              >
                {referencia.titulo}
              </th>
              {/* El original en gris y el homologado en el color del texto: uno
                  es el punto de partida y el otro la cifra que entra al
                  informe. */}
              <td className="text-superficie-fija-muted py-1.5 text-right font-mono tabular-nums">
                {original === null ? "—" : numero(original, 0)}
              </td>
              <td className="py-1.5 text-right font-mono tabular-nums">
                {homologado === null ? "—" : numero(homologado, 0)}
              </td>
            </tr>
          ))}
        </tbody>

        {/* CIERRA EL CÍRCULO: esta media homologada es exactamente la cifra
            grande de arriba. Es lo que vuelve legible al panel — sin la fila,
            la tabla sería una lista más. Su par sin homologar dice, de paso,
            cuánto movió la homologación al conjunto entero. */}
        <tfoot>
          <tr className="border-superficie-fija-linea border-t-[1.5px]">
            <th scope="row" className="text-superficie-fija-foreground py-2 text-left font-bold">
              Media
            </th>
            <td className="text-superficie-fija-muted py-2 text-right font-mono font-bold tabular-nums">
              {mediaOriginal === null ? "—" : numero(mediaOriginal, 0)}
            </td>
            {/* Verde, como el valor total del predio: las dos son cifras
                DERIVADAS y no un dato leído de las fichas. */}
            <td className="text-superficie-fija-exito py-2 text-right font-mono font-bold tabular-nums">
              {mediaUfHa === null ? "—" : numero(mediaUfHa, 0)}
            </td>
          </tr>
        </tfoot>
      </table>

      {sinHomologar > 0 && (
        // ADVIERTE Y NO BLOQUEA, misma regla que el aviso de validación del
        // enlazador: homologar es criterio del tasador. Lo que se dice es que la
        // cifra grande está mezclando comparables corregidos con otros que no.
        <div className="bg-superficie-fija-aviso-fondo text-superficie-fija-aviso mt-3.5 flex gap-2.5 rounded-xl px-3.5 py-3 text-[0.78125rem] leading-snug font-semibold text-pretty">
          <TriangleAlert className="mt-px size-4 flex-none" aria-hidden />
          <span>
            {sinHomologar === 1
              ? "1 ficha sigue sin homologar: entra a la media con su valor de catálogo."
              : `${sinHomologar} fichas siguen sin homologar: entran a la media con su valor de catálogo.`}
          </span>
        </div>
      )}
    </div>
  );
}

function Fila({
  rotulo,
  valor,
  className,
}: {
  rotulo: string;
  valor: string;
  className?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-superficie-fija-rotulo">{rotulo}</dt>
      <dd className={cn("font-mono tabular-nums", className)}>{valor}</dd>
    </div>
  );
}

/**
 * UF por hectárea de una referencia, derivado al leer.
 *
 * Repite la cuenta de `FichaDeReferencia` a propósito y no se comparte todavía:
 * son dos lecturas del mismo dato congelado y extraerlas ahora obligaría a
 * elegir dónde vive el helper antes de que exista un tercer llamador.
 */
function ufHa(referencia: ReferenciaDelInforme): number | null {
  const uf = Number(referencia.precio_uf);
  const m2 = Number(referencia.superficie_terreno_m2);

  if (!Number.isFinite(uf) || !Number.isFinite(m2) || m2 <= 0) return null;

  return uf / (m2 / 10_000);
}
